import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { fmtMoney } from "@/lib/leadMeta";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Loader2, ShieldCheck, Mail, Send, Clock, X, PhoneCall, Trophy } from "lucide-react";
import { dialHref } from "@/lib/dialer";
import { formatPhone } from "@/lib/phone";

const ROLE_COLORS = { admin: "#0D9488", manager: "#8B5CF6", agent: "#3B82F6" };
const JOIN_SOURCE = { qr: { label: "Joined via QR", cls: "text-sky-500" }, link: { label: "Joined via link", cls: "text-violet-500" }, manual: { label: "Joined via code", cls: "text-muted-foreground" } };

const ROLE_ITEMS = (
  <>
    <SelectItem value="agent">Agent — works assigned & shared leads</SelectItem>
    <SelectItem value="manager">Manager — full lead access, assigns</SelectItem>
    <SelectItem value="admin">Admin — full control</SelectItem>
  </>
);

function InviteMember({ onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "agent" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required");
    setLoading(true);
    try {
      const { data } = await api.post("/team/invite", form);
      toast.success(data.emailed ? `Invitation emailed to ${form.email}` : `Invite created for ${form.email} (email delivery pending)`);
      setForm({ name: "", email: "", role: "agent" });
      setOpen(false);
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="invite-member-button"><Send className="mr-2 h-4 w-4" /> Invite teammate</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Email an invitation</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">They'll get an email link to set their own password and join your agency.</p>
        <div className="space-y-4">
          <div><Label>Name</Label><Input value={form.name} onChange={set("name")} className="mt-1.5" data-testid="invite-name-input" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} className="mt-1.5" data-testid="invite-email-input" /></div>
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={set("role")}>
              <SelectTrigger className="mt-1.5" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLE_ITEMS}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={loading} data-testid="send-invite-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send invite</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddMember({ onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "agent" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/team", form);
      toast.success(`${form.name} added to your team`);
      setForm({ name: "", email: "", password: "", role: "agent" });
      setOpen(false);
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="add-member-button"><UserPlus className="mr-2 h-4 w-4" /> Add directly</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Add member with a password</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Name</Label><Input value={form.name} onChange={set("name")} className="mt-1.5" data-testid="member-name-input" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} className="mt-1.5" data-testid="member-email-input" /></div>
          <div><Label>Temporary password</Label><Input value={form.password} onChange={set("password")} className="mt-1.5" placeholder="Min. 6 characters" data-testid="member-password-input" /></div>
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={set("role")}>
              <SelectTrigger className="mt-1.5" data-testid="member-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLE_ITEMS}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={loading} data-testid="save-member-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add member</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Leaderboard() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/leads/closer-leaderboard").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  if (rows.length === 0) return null;
  const medal = ["text-amber-400", "text-slate-400", "text-amber-700"];
  return (
    <Card className="p-5" data-testid="closer-leaderboard">
      <h3 className="flex items-center gap-2 font-heading font-semibold"><Trophy className="h-4 w-4 text-amber-500" /> Closer leaderboard</h3>
      <p className="mt-1 text-xs text-muted-foreground">Ranked by leads won, with each closer's projected commission from won deals.</p>
      <div className="mt-3 space-y-2">
        {rows.map((r, i) => (
          <div key={r.user_id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2" data-testid={`leaderboard-row-${r.user_id}`}>
            <span className={`w-5 text-center font-mono text-sm font-bold ${medal[i] || "text-muted-foreground"}`}>{i + 1}</span>
            <Avatar className="h-8 w-8"><AvatarFallback className="bg-muted text-xs">{r.name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1 truncate text-sm font-medium">{r.name} {r.is_closer && <Badge variant="outline" className="text-[10px]">Closer</Badge>}</div>
              <p className="text-xs capitalize text-muted-foreground">{r.role}</p>
            </div>
            <div className="text-right">
              <span className="block font-mono text-sm font-bold text-emerald-500" data-testid={`leaderboard-wins-${r.user_id}`}>{r.wins} won</span>
              {r.commission > 0 && <span className="block font-mono text-xs text-primary" data-testid={`leaderboard-commission-${r.user_id}`}>{fmtMoney(r.commission)}</span>}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function Team() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [team, setTeam] = useState([]);
  const [invites, setInvites] = useState([]);

  const load = () => api.get("/team").then(({ data }) => setTeam(data)).catch(() => {});
  const loadInvites = () => { if (isAdmin) api.get("/team/invites").then(({ data }) => setInvites(data)).catch(() => {}); };
  const refreshAll = () => { load(); loadInvites(); };
  useEffect(() => { refreshAll(); }, []); // eslint-disable-line

  const updateRole = async (m, role) => {
    try { await api.patch(`/team/${m.user_id}`, { role }); toast.success("Role updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleActive = async (m) => {
    try { await api.patch(`/team/${m.user_id}`, { active: !m.active }); toast.success(m.active ? "Member deactivated" : "Member reactivated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const revokeInvite = async (inv) => {
    try { await api.delete(`/team/invites/${inv.invite_id}`); toast.success("Invite revoked"); loadInvites(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold sm:text-3xl">Team</h1>
          <p className="mt-1 text-sm text-muted-foreground">{team.length} member{team.length !== 1 ? "s" : ""} in your agency.</p>
        </div>
        {isAdmin && <div className="flex gap-2"><InviteMember onDone={loadInvites} /><AddMember onDone={load} /></div>}
      </div>

      {isAdmin && invites.length > 0 && (
        <Card className="p-5" data-testid="pending-invites">
          <h3 className="flex items-center gap-2 font-heading font-semibold"><Clock className="h-4 w-4 text-amber-500" /> Pending invitations ({invites.length})</h3>
          <div className="mt-3 space-y-2">
            {invites.map((inv) => (
              <div key={inv.invite_id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-4 py-2.5" data-testid={`invite-row-${inv.invite_id}`}>
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <span className="flex items-center gap-2 font-medium"><Mail className="h-4 w-4 text-muted-foreground" /> {inv.name}</span>
                  <span className="truncate text-muted-foreground">{inv.email}</span>
                  <Badge variant="outline" className="capitalize">{inv.role}</Badge>
                </div>
                <Button size="icon" variant="ghost" onClick={() => revokeInvite(inv)} data-testid={`revoke-invite-${inv.invite_id}`} className="shrink-0 text-destructive hover:text-destructive"><X className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm" data-testid="team-table">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
              <th className="px-4 py-3 font-medium">Member</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Assigned leads</th>
              <th className="px-4 py-3 font-medium">Status</th>
              {isAdmin && <th className="px-4 py-3 text-right font-medium">Active</th>}
            </tr>
          </thead>
          <tbody>
            {team.map((m) => (
              <tr key={m.user_id} className="border-b border-border/60" data-testid={`team-row-${m.user_id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9"><AvatarFallback className="bg-muted text-xs">{m.name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
                    <div>
                      <p className="font-medium">{m.name} {m.user_id === user.user_id && <span className="text-xs text-muted-foreground">(you)</span>}</p>
                      <p className="text-xs text-muted-foreground">{m.email}</p>
                      {m.join_source && <p className={`text-[11px] ${JOIN_SOURCE[m.join_source]?.cls || "text-muted-foreground"}`} data-testid={`team-source-${m.user_id}`}>{JOIN_SOURCE[m.join_source]?.label || "Joined via code"}</p>}
                      {m.phone && (
                        <a href={dialHref(m.phone, user)} data-testid={`team-call-${m.user_id}`} className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                          <PhoneCall className="h-3 w-3" /> {formatPhone(m.phone)}
                        </a>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {isAdmin && m.user_id !== user.user_id ? (
                    <Select value={m.role} onValueChange={(v) => updateRole(m, v)}>
                      <SelectTrigger className="w-32" data-testid={`team-member-role-select-${m.user_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="agent">Agent</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge style={{ backgroundColor: ROLE_COLORS[m.role] + "22", color: ROLE_COLORS[m.role] }} className="capitalize">
                      {m.role === "admin" && <ShieldCheck className="mr-1 h-3 w-3" />}{m.role}
                    </Badge>
                  )}
                </td>
                <td className="px-4 py-3 font-mono">{m.lead_count}</td>
                <td className="px-4 py-3">
                  <span className={m.active ? "text-emerald-500" : "text-muted-foreground"}>{m.active ? "Active" : "Inactive"}</span>
                </td>
                {isAdmin && (
                  <td className="px-4 py-3 text-right">
                    {m.user_id !== user.user_id && <Switch checked={m.active} onCheckedChange={() => toggleActive(m)} data-testid={`team-active-switch-${m.user_id}`} />}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        <div className="divide-y divide-border/60 md:hidden" data-testid="team-cards">
          {team.map((m) => (
            <div key={m.user_id} className="p-4" data-testid={`team-card-${m.user_id}`}>
              <div className="flex items-center gap-3">
                <Avatar className="h-9 w-9"><AvatarFallback className="bg-muted text-xs">{m.name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{m.name} {m.user_id === user.user_id && <span className="text-xs text-muted-foreground">(you)</span>}</p>
                  <p className="truncate text-xs text-muted-foreground">{m.email}</p>
                  {m.join_source && <p className={`text-[11px] ${JOIN_SOURCE[m.join_source]?.cls || "text-muted-foreground"}`} data-testid={`team-source-m-${m.user_id}`}>{JOIN_SOURCE[m.join_source]?.label || "Joined via code"}</p>}
                  {m.phone && (
                    <a href={dialHref(m.phone, user)} data-testid={`team-call-m-${m.user_id}`} className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                      <PhoneCall className="h-3 w-3" /> {formatPhone(m.phone)}
                    </a>
                  )}
                </div>
                <Badge style={{ backgroundColor: ROLE_COLORS[m.role] + "22", color: ROLE_COLORS[m.role] }} className="capitalize">{m.role}</Badge>
              </div>
              <div className="mt-3 flex items-center justify-between gap-3 text-sm">
                <span className="text-muted-foreground">{m.lead_count} leads · {m.active ? "Active" : "Inactive"}</span>
                {isAdmin && m.user_id !== user.user_id && (
                  <div className="flex items-center gap-2">
                    <Select value={m.role} onValueChange={(v) => updateRole(m, v)}>
                      <SelectTrigger className="h-8 w-28" data-testid={`team-member-role-select-m-${m.user_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="agent">Agent</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                    <Switch checked={m.active} onCheckedChange={() => toggleActive(m)} data-testid={`team-active-switch-m-${m.user_id}`} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Leaderboard />

      {!isAdmin && <p className="text-sm text-muted-foreground">Only agency admins can invite or manage team members.</p>}
    </div>
  );
}
